import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import {
  remainingDailyNewGraphemes,
  type ResolvedDailyNewGraphemeLimit,
  resolveDailyNewGraphemeLimit,
} from "#daily-new-grapheme-limit";
import {
  GRAPHEMES_INTRODUCED_TODAY_TABLES,
  useGraphemesIntroducedTodayQueryOptions,
} from "#use-graphemes-introduced-today-query";

export type DailyNewGraphemeAllowance = ResolvedDailyNewGraphemeLimit & {
  introducedTodayCount: number;
  /** Today's limit is used up. Never true without a limit. */
  isLimitReached: boolean;
  /** New graphemes that may still be introduced today; `Infinity` without a limit. */
  remaining: number;
};

export function useGetDailyNewGraphemeAllowanceQueryKey() {
  const isPremium = useIsPremium();
  const graphemesIntroducedTodayQueryOptions = useGraphemesIntroducedTodayQueryOptions();
  // The learner's own limit lives on the `user` row; its options key carries that table's version.
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(GRAPHEMES_INTRODUCED_TODAY_TABLES),
  );
  return ({ isPremium: isPremiumOverride }: { isPremium?: boolean } = {}) =>
    withTableChangeVersion([
      "dailyNewGraphemeAllowance",
      {
        actorUserDataQueryOptions,
        graphemesIntroducedTodayQueryOptions,
        isPremium: isPremiumOverride ?? isPremium,
      },
    ] as const);
}

/**
 * Today's new-grapheme allowance under the learner's Daily New Cards setting, held to
 * {@link FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT} for a free learner (see
 * {@link resolveDailyNewGraphemeLimit}).
 */
export const [useDailyNewGraphemeAllowanceQueryOptions, dailyNewGraphemeAllowanceQueryFn] =
  createUseQueryOptions(
    useGetDailyNewGraphemeAllowanceQueryKey,
    async ({
      client,
      queryKey: [_, { actorUserDataQueryOptions, graphemesIntroducedTodayQueryOptions, isPremium }],
    }): Promise<DailyNewGraphemeAllowance> => {
      const [introducedTodayCount, { user }] = await Promise.all([
        client.fetchQuery(graphemesIntroducedTodayQueryOptions),
        client.fetchQuery(actorUserDataQueryOptions),
      ]);
      const resolvedLimit = resolveDailyNewGraphemeLimit({
        dailyNewGraphemeLimit: user.dailyNewGraphemeLimit,
        isPremium,
      });
      const remaining = remainingDailyNewGraphemes({
        introducedTodayCount,
        limit: resolvedLimit.limit,
      });
      return { ...resolvedLimit, introducedTodayCount, isLimitReached: remaining === 0, remaining };
    },
  );
